import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft } from 'lucide-react';
import Navigation from '../components/Navigation';
import { useTranslation } from 'react-i18next';

// ---- Pricing (CAD, HST 13%) ----
const HST = 0.13;

// ✅ One-time group creation fee = $15 CAD (before tax)
const ONE_TIME_BASE = 15.0;
const ONE_TIME_TOTAL = Number((ONE_TIME_BASE * (1 + HST)).toFixed(2)); // tax-in

// ✅ Final, clean pricing ladder (what you keep)
const TIERS = [
  {
    id: 'community_1_100',
    label: 'Community — up to 100 members',
    monthlyBase: 25.0,
    monthlyTotal: Number((25.0 * (1 + HST)).toFixed(2)),
    subtitle: 'Best for most groups',
    detail: '$25.00 + HST',
    type: 'fixed',
  },
  {
    id: 'growth_101_300',
    label: 'Growth — up to 300 members',
    monthlyBase: 45.0,
    monthlyTotal: Number((45.0 * (1 + HST)).toFixed(2)),
    subtitle: 'For larger and active communities',
    detail: '$45.00 + HST',
    type: 'fixed',
  },
  {
    id: 'custom_300_plus',
    label: 'Custom — 300+ members',
    monthlyBase: null,
    monthlyTotal: null,
    subtitle: 'We’ll tailor pricing for your size',
    detail: 'Contact us for a custom plan',
    type: 'custom',
  },
];

// Plan-based caps (only for fixed tiers)
const TIER_MAX_MEMBERS = {
  community_1_100: 100,
  growth_101_300: 300,
};

function fmtCAD(n) {
  try {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Number(n || 0));
  } catch {
    return `$${Number(n || 0).toFixed(2)}`;
  }
}

const CreateGroup = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);

  // group form
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    membership_price: 0,
    membership_period: 1,
    currency: 'CAD',
    visibility: 'public',
    require_subscription: false,
    max_capacity: '',
  });

  // plan dialog
  const [openPlan, setOpenPlan] = useState(false);
  const [selectedTierId, setSelectedTierId] = useState('community_1_100');
  const [billingCycle, setBillingCycle] = useState('monthly'); // 'monthly' | 'yearly'

  const selectedTier = TIERS.find((tTier) => tTier.id === selectedTierId);

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Open plan selector before checkout
    setOpenPlan(true);
  };

  const startCheckout = async () => {
    try {
      setIsLoading(true);

      const discountRate = billingCycle === 'yearly' ? 0.1 : 0;

      const tierPayload =
        selectedTier.type === 'fixed'
          ? {
              tier_id: selectedTier.id,
              tier_label: selectedTier.label,
              monthly_base: selectedTier.monthlyBase,
              monthly_total: selectedTier.monthlyTotal,
              pricing_type: 'fixed',
              billing_cycle: billingCycle,
              discount_rate: discountRate,
              yearly_total:
                discountRate > 0
                  ? selectedTier.monthlyTotal * 12 * (1 - discountRate)
                  : null,
            }
          : {
              tier_id: selectedTier.id,
              tier_label: selectedTier.label,
              pricing_type: 'custom',
              billing_cycle: billingCycle,
              discount_rate: discountRate,
            };

      // Enforce member cap based on selected plan & max_capacity field (fixed tiers only)
      let effectiveMaxCapacity =
        formData.max_capacity === '' || formData.max_capacity == null
          ? null
          : Number(formData.max_capacity);

      if (selectedTier.type === 'fixed') {
        const tierCap = TIER_MAX_MEMBERS[selectedTier.id] || null;
        if (tierCap) {
          if (!effectiveMaxCapacity || effectiveMaxCapacity > tierCap) {
            effectiveMaxCapacity = tierCap;
          }
        }
      }
      // If custom tier, we don't auto-cap (admin can set max_capacity manually if desired)

      const payload = {
        ...formData,
        currency: 'CAD', // backend expects CAD for group-create
        max_capacity: effectiveMaxCapacity,
        require_subscription: !!formData.require_subscription,
        pricing_selection: tierPayload,

        // ✅ one-time group creation fee
        one_time_fee_base: ONE_TIME_BASE,
        one_time_fee_total: ONE_TIME_TOTAL,
        one_time_fee_tax_rate: HST,
      };

      const res = await api.post('/checkout/group-create', payload);
      window.location.href = res.data.url;
    } catch (error) {
      toast.error(
        error?.response?.data?.detail ||
          t('createGroup.errors.checkout', {
            defaultValue: 'Failed to start checkout',
          })
      );
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <Navigation />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-4"
          data-testid="back-btn"
        >
          <ArrowLeft size={16} className="mr-2" />
          {t('createGroup.back', { defaultValue: 'Back to previous page' })}
        </Button>

        <Card>
          <CardHeader>
            <CardTitle className="text-2xl">
              {t('createGroup.title', { defaultValue: 'Create a new group' })}
            </CardTitle>
            <CardDescription>
              {t('createGroup.subtitle', {
                defaultValue:
                  'Set up your community space, membership details, and visibility.',
              })}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name">
                  {t('createGroup.fields.nameLabel', {
                    defaultValue: 'Group name',
                  })}{' '}
                  *
                </Label>
                <Input
                  id="name"
                  placeholder={t('createGroup.fields.namePlaceholder', {
                    defaultValue: 'Example: Oakwood Community Association',
                  })}
                  value={formData.name}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  required
                  data-testid="group-name-input"
                />
              </div>

              <div>
                <Label htmlFor="description">
                  {t('createGroup.fields.descriptionLabel', {
                    defaultValue: 'Description',
                  })}{' '}
                  *
                </Label>
                <Textarea
                  id="description"
                  placeholder={t('createGroup.fields.descriptionPlaceholder', {
                    defaultValue:
                      'Briefly describe your community, who it’s for, and what you do.',
                  })}
                  value={formData.description}
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      description: e.target.value,
                    }))
                  }
                  rows={4}
                  required
                  data-testid="group-description-input"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="membership-price">
                    {t('createGroup.fields.defaultPriceLabel', {
                      defaultValue: 'Default membership price (optional)',
                    })}
                  </Label>
                  <Input
                    id="membership-price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    value={formData.membership_price}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        membership_price: e.target.value, // keep as string
                      }))
                    }
                    onBlur={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        membership_price:
                          e.target.value === '' ? '' : Number(e.target.value),
                      }))
                    }
                    data-testid="membership-price-input"
                  />
                </div>

                <div>
                  <Label htmlFor="currency">
                    {t('createGroup.fields.currencyLabel', {
                      defaultValue: 'Currency',
                    })}
                  </Label>
                  <Select
                    value={formData.currency}
                    onValueChange={(value) =>
                      setFormData((prev) => ({ ...prev, currency: value }))
                    }
                  >
                    <SelectTrigger id="currency" data-testid="currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CAD">CAD</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                      <SelectItem value="EUR">EUR</SelectItem>
                      <SelectItem value="GBP">GBP</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Subscription requirement + max capacity */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="flex items-center justify-between border rounded-lg px-3 py-2">
                  <div>
                    <Label className="font-medium">
                      {t('createGroup.requireSubscriptionLabel', {
                        defaultValue: 'Require subscription to join',
                      })}
                    </Label>
                    <p className="text-xs text-slate-500">
                      {t('createGroup.requireSubscriptionHelp', {
                        defaultValue:
                          'Members must have an active paid subscription to join this group.',
                      })}
                    </p>
                  </div>
                  <Switch
                    checked={formData.require_subscription}
                    onCheckedChange={(value) =>
                      setFormData((prev) => ({
                        ...prev,
                        require_subscription: value,
                      }))
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="max-capacity">
                    {t('createGroup.maxMembersLabel', {
                      defaultValue: 'Max members (optional)',
                    })}
                  </Label>
                  <Input
                    id="max-capacity"
                    type="number"
                    min="1"
                    placeholder={t('createGroup.maxMembersPlaceholder', {
                      defaultValue: 'e.g. 150',
                    })}
                    value={formData.max_capacity}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        max_capacity: e.target.value,
                      }))
                    }
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    {t('createGroup.maxMembersHelp', {
                      defaultValue:
                        'When this limit is reached, new join requests will be blocked and admins will be notified by email. Your selected plan will cap this value (for fixed plans).',
                    })}
                  </p>
                </div>
              </div>

              <div>
                <Label htmlFor="visibility">
                  {t('createGroup.fields.visibilityLabel', {
                    defaultValue: 'Visibility',
                  })}
                </Label>
                <Select
                  value={formData.visibility}
                  onValueChange={(value) =>
                    setFormData((prev) => ({ ...prev, visibility: value }))
                  }
                >
                  <SelectTrigger id="visibility" data-testid="visibility-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="public">
                      {t('createGroup.fields.visibilityPublic', {
                        defaultValue: 'Public (listed for everyone)',
                      })}
                    </SelectItem>
                    <SelectItem value="private">
                      {t('createGroup.fields.visibilityPrivate', {
                        defaultValue: 'Private (invite or link only)',
                      })}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="flex-1"
                  data-testid="cancel-btn"
                >
                  {t('createGroup.actions.cancel', {
                    defaultValue: 'Cancel',
                  })}
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={isLoading || !formData.name || !formData.description}
                  data-testid="create-group-submit-btn"
                >
                  {isLoading
                    ? t('createGroup.actions.startingCheckout', {
                        defaultValue: 'Starting checkout...',
                      })
                    : t('createGroup.actions.create', {
                        defaultValue: 'Create group & continue',
                      })}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>

      {/* Plan Selection Dialog */}
      <Dialog open={openPlan} onOpenChange={setOpenPlan}>
        <DialogContent className="sm:max-w-lg w-[95vw] max-h-[85vh] flex flex-col overflow-hidden">
          <DialogHeader>
            <DialogTitle>
              {t('createGroup.plan.title', {
                defaultValue: 'Choose your ZiiHub plan',
              })}
            </DialogTitle>
            <DialogDescription>
              {t('createGroup.plan.description', {
                amount: ONE_TIME_TOTAL.toFixed(2),
                defaultValue:
                  'To create your group, there is a one-time fee of {{amount}} CAD (tax included). Then choose the plan that matches your community.',
              })}
            </DialogDescription>
          </DialogHeader>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto pr-2">
            <div className="space-y-4">
              {/* Monthly / Yearly toggle */}
              <div className="sticky top-0 z-10 bg-white/90 backdrop-blur py-2">
                <div className="flex items-center justify-center gap-2 text-sm">
                  <Button
                    type="button"
                    variant={billingCycle === 'monthly' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBillingCycle('monthly')}
                  >
                    {t('createGroup.plan.monthly', { defaultValue: 'Monthly' })}
                  </Button>

                  <Button
                    type="button"
                    variant={billingCycle === 'yearly' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setBillingCycle('yearly')}
                  >
                    {t('createGroup.plan.yearly', { defaultValue: 'Yearly' })}{' '}
                    <span className="ml-1 text-xs font-semibold">
                      {t('createGroup.plan.yearlyDiscount', {
                        defaultValue: '(10% off)',
                      })}
                    </span>
                  </Button>
                </div>
              </div>

              {/* Tier options */}
              <RadioGroup value={selectedTierId} onValueChange={setSelectedTierId}>
                {TIERS.map((tier) => (
                  <label
                    key={tier.id}
                    className="flex items-start gap-3 rounded-xl border p-3 cursor-pointer hover:bg-slate-50"
                  >
                    <RadioGroupItem value={tier.id} className="mt-1" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{tier.label}</span>
                        {tier.type === 'custom' ? (
                          <Badge variant="outline">
                            {t('createGroup.plan.customBadge', {
                              defaultValue: 'Custom',
                            })}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            {t('createGroup.plan.fixedBadge', {
                              defaultValue: 'Fixed',
                            })}
                          </Badge>
                        )}
                      </div>

                      <div className="text-sm text-slate-600">{tier.subtitle}</div>

                      <div className="text-sm text-slate-700 mt-1">
                        {tier.type === 'fixed' ? (
                          <>
                            {tier.detail}{' '}
                            <span className="text-slate-500">
                              ({fmtCAD(tier.monthlyTotal)}/mo tax-in)
                            </span>

                            {billingCycle === 'yearly' && (
                              <div className="mt-1">
                                <div>
                                  {t('createGroup.plan.yearlyLabel', {
                                    defaultValue: 'Yearly (10% off):',
                                  })}{' '}
                                  <strong>{fmtCAD(tier.monthlyTotal * 12 * 0.9)}</strong>{' '}
                                  /{t('createGroup.plan.perYear', {
                                    defaultValue: 'year',
                                  })}
                                </div>
                                <div className="text-xs text-emerald-700">
                                  {t('createGroup.plan.yearlySavings', {
                                    defaultValue:
                                      'Save 10% compared to paying monthly.',
                                  })}
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <span>{tier.detail}</span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </RadioGroup>

              {/* Summary */}
              <div className="rounded-lg bg-slate-50 p-3 text-sm">
                <div className="font-medium mb-1">
                  {t('createGroup.plan.summaryTitle', {
                    defaultValue: 'Summary',
                  })}
                </div>
                <p>
                  {t('createGroup.plan.summaryOneTime', {
                    amount: fmtCAD(ONE_TIME_TOTAL),
                    defaultValue:
                      'You’ll pay a one-time group creation fee of {{amount}} today.',
                  })}
                </p>
                <p className="mt-1 text-slate-600">
                  {t('createGroup.plan.summaryBody', {
                    defaultValue:
                      'Your plan starts after your group is created. For fixed plans, member capacity is capped by the plan and can be lowered (but not raised above the plan) from settings.',
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Footer buttons */}
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setOpenPlan(false)}>
              {t('back', { defaultValue: 'Back' })}
            </Button>
            <Button onClick={startCheckout} disabled={isLoading}>
              {isLoading
                ? t('createGroup.plan.redirecting', { defaultValue: 'Redirecting…' })
                : t('createGroup.plan.continue', {
                    defaultValue: 'Continue to checkout',
                  })}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateGroup;